import { HttpInterceptorFn } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { hostedGatewayOriginsForHostname } from '../../../environments/gateway-origins';

export const API_ORIGIN_PREFIXES = ['/api', '/Auth', '/media', '/streams'] as const;

/** True when a relative path should be prefixed with a gateway origin. */
export function matchesApiOriginPrefix(path: string): boolean {
  return API_ORIGIN_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}

/** STS `/Auth` uses `authOrigin` when set so UAT login does not hit the DEV gateway. */
export function gatewayOriginForPath(path: string, apiOrigin: string, authOrigin: string): string {
  const api = apiOrigin.replace(/\/$/, '');
  const auth = (authOrigin || apiOrigin).replace(/\/$/, '');
  if (path === '/Auth' || path.startsWith('/Auth/')) {
    return auth;
  }
  return api;
}

/**
 * Maps app-relative API paths to the public gateway (mirrors `proxy.dev.conf.json` rewrites).
 * `/api/dealer` → `{origin}/dealer`, `/api/resolve` → `{origin}/resolve`, `/Auth` unchanged.
 */
export function resolveGatewayRequestUrl(path: string, origin: string): string {
  const base = origin.replace(/\/$/, '');

  if (path === '/api/dealer' || path.startsWith('/api/dealer/')) {
    return `${base}/dealer${path.slice('/api/dealer'.length)}`;
  }
  if (path === '/api/resolve' || path.startsWith('/api/resolve/')) {
    return `${base}/resolve${path.slice('/api/resolve'.length)}`;
  }

  return `${base}${path}`;
}

/** Prefixes same-origin backend paths with the hosted gateway origin when set. */
export const apiOriginInterceptor: HttpInterceptorFn = (req, next) => {
  const path = req.url;
  if (!matchesApiOriginPrefix(path)) {
    return next(req);
  }

  const hosted = hostedGatewayOriginsForHostname(globalThis.location?.hostname ?? '');
  const origin = gatewayOriginForPath(
    path,
    hosted?.apiOrigin ?? environment.apiOrigin,
    hosted?.authOrigin ?? environment.authOrigin,
  );
  if (!origin) {
    return next(req);
  }

  return next(req.clone({ url: resolveGatewayRequestUrl(path, origin) }));
};
